import { useState } from 'react';
import { Alert, Flex, Loader } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import PageWithSidebar from 'src/UI/page-nav/PageWithSidebar';
import { useFamilyMeetings } from 'src/meetings/hooks/useFamilyMeetings';
import { isPastEvent } from 'src/meetings/utils/date-utils';
import { AddExpectedAttendeesModal } from './AddExpectedAttendeesModal';
import { FamilyMeetingsList } from './FamilyMeetingsList';
import { GenerateMeetingNoteModal } from './GenerateMeetingNoteModal';

type Props = {
  familyId: string;
  section?: string;
};

const SECTIONS = {
  Upcoming: 'upcoming',
  Past: 'past',
} as const;

export function FamilyMeetingsPanel({ familyId, section }: Props) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { events, isLoading, error } = useFamilyMeetings(familyId);
  const [participantsMeetingId, setParticipantsMeetingId] = useState<string | null>(null);
  const [noteMeetingId, setNoteMeetingId] = useState<string | null>(null);

  if (section !== SECTIONS.Upcoming && section !== SECTIONS.Past) {
    const query = searchParams.toString();
    return (
      <Navigate
        to={`/family/${familyId}/meetings/${SECTIONS.Upcoming}${query ? `?${query}` : ''}`}
        replace
      />
    );
  }

  const isPastSection = section === SECTIONS.Past;

  const links = [
    { label: 'Upcoming', href: `/family/${familyId}/meetings/${SECTIONS.Upcoming}` },
    { label: 'Past meetings', href: `/family/${familyId}/meetings/${SECTIONS.Past}` },
  ];

  const sectionEvents = events.filter(event => isPastEvent(event) === isPastSection);

  const participantsMeeting = events.find(event => event.id === participantsMeetingId) ?? null;
  const noteMeeting = events.find(event => event.id === noteMeetingId) ?? null;

  function renderBody() {
    if (isLoading) {
      return (
        <Flex py="xxl" justify="center">
          <Loader />
        </Flex>
      );
    }

    if (error) {
      return (
        <Alert variant="light" color="yellow" icon={<IconAlertTriangle size={18} />}>
          Some meetings could not be loaded.
        </Alert>
      );
    }

    return (
      <FamilyMeetingsList
        meetings={sectionEvents}
        order={isPastSection ? 'desc' : 'asc'}
        emptyMessage={
          isPastSection
            ? 'No past meetings to show for this family yet.'
            : 'No upcoming meetings to show for this family yet.'
        }
        onOpenParticipants={setParticipantsMeetingId}
        onOpenDetails={eventId => navigate(`/family/${familyId}/meeting/${eventId}`)}
        onGenerateMeetingNote={setNoteMeetingId}
      />
    );
  }

  return (
    <>
      <PageWithSidebar
        links={links}
        activeUrl={`/family/${familyId}/meetings/${section}`}
        variant="profileV2"
      >
        {renderBody()}
      </PageWithSidebar>
      {participantsMeeting && (
        <AddExpectedAttendeesModal
          opened
          onClose={() => setParticipantsMeetingId(null)}
          familyId={familyId}
          meeting={participantsMeeting}
        />
      )}
      {noteMeeting && (
        <GenerateMeetingNoteModal
          opened
          onClose={() => setNoteMeetingId(null)}
          familyId={familyId}
          meeting={noteMeeting}
        />
      )}
    </>
  );
}
